import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Save, 
  X, 
  TreePine,
  PawPrint,
  AlertTriangle,
  Shield,
  Bird,
  Bug,
  Cat,
  Dog,
  Fish,
  Rabbit,
  Squirrel,
  Leaf,
  Mountain,
  Compass,
  Target,
  Eye,
  Heart,
  Star,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available icons for selection
const availableIcons = [
  { name: 'PawPrint', component: PawPrint, label: 'Taca' },
  { name: 'TreePine', component: TreePine, label: 'Drevo' },
  { name: 'AlertTriangle', component: AlertTriangle, label: 'Opozorilo' },
  { name: 'Shield', component: Shield, label: 'Ščit' },
  { name: 'Bird', component: Bird, label: 'Ptica' },
  { name: 'Bug', component: Bug, label: 'Žuželka' },
  { name: 'Cat', component: Cat, label: 'Mačka' },
  { name: 'Dog', component: Dog, label: 'Pes' },
  { name: 'Fish', component: Fish, label: 'Riba' },
  { name: 'Rabbit', component: Rabbit, label: 'Zajec' },
  { name: 'Squirrel', component: Squirrel, label: 'Veverica' },
  { name: 'Leaf', component: Leaf, label: 'List' },
  { name: 'Mountain', component: Mountain, label: 'Gora' },
  { name: 'Compass', component: Compass, label: 'Kompas' },
  { name: 'Target', component: Target, label: 'Tarča' },
  { name: 'Eye', component: Eye, label: 'Oko' },
  { name: 'Heart', component: Heart, label: 'Srce' },
  { name: 'Star', component: Star, label: 'Zvezda' },
];

const availableColors = [
  { name: 'emerald', label: 'Zelena', class: 'bg-emerald-600' },
  { name: 'amber', label: 'Jantarna', class: 'bg-amber-600' },
  { name: 'orange', label: 'Oranžna', class: 'bg-orange-600' },
  { name: 'red', label: 'Rdeča', class: 'bg-red-600' },
  { name: 'blue', label: 'Modra', class: 'bg-blue-600' },
  { name: 'purple', label: 'Vijolična', class: 'bg-purple-600' },
  { name: 'green', label: 'Temno zelena', class: 'bg-green-700' },
  { name: 'yellow', label: 'Rumena', class: 'bg-yellow-600' },
];

const getIconComponent = (iconName) => {
  const found = availableIcons.find(i => i.name === iconName);
  return found ? found.component : PawPrint;
};

const AdminWildlife = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title_sl: '',
    title_en: '',
    content_sl: '',
    content_en: '',
    icon: 'PawPrint',
    color: 'emerald',
    order: 0
  });

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API}/wildlife`);
      if (response.data && response.data.length > 0) {
        setSections(response.data);
      } else {
        await seedWildlife();
      }
    } catch (error) {
      console.error('Error fetching wildlife:', error);
      await seedWildlife();
    } finally {
      setLoading(false);
    }
  };

  const seedWildlife = async () => {
    try {
      await axios.post(`${API}/seed-wildlife`);
      const response = await axios.get(`${API}/wildlife`);
      setSections(response.data);
      toast.success('Sekcije divjadi so bile uvožene');
    } catch (error) {
      console.error('Error seeding wildlife:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        await axios.put(`${API}/wildlife/${editingId}`, formData);
        toast.success('Sekcija je bila posodobljena');
      } else {
        const newOrder = sections.length + 1;
        await axios.post(`${API}/wildlife`, { ...formData, order: newOrder });
        toast.success('Nova sekcija je bila dodana');
      }
      
      resetForm();
      fetchSections();
    } catch (error) {
      console.error('Error saving section:', error);
      toast.error('Napaka pri shranjevanju');
    }
  };

  const handleEdit = (section) => {
    setEditingId(section.id);
    setFormData({
      title_sl: section.title_sl,
      title_en: section.title_en,
      content_sl: section.content_sl,
      content_en: section.content_en,
      icon: section.icon || 'PawPrint',
      color: section.color || 'emerald',
      order: section.order
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ali ste prepričani, da želite izbrisati to sekcijo?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/wildlife/${id}`);
      toast.success('Sekcija je bila izbrisana');
      fetchSections();
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error('Napaka pri brisanju');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setShowAddForm(false);
    setFormData({
      title_sl: '',
      title_en: '',
      content_sl: '',
      content_en: '',
      icon: 'PawPrint',
      color: 'emerald',
      order: 0
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  const IconComponent = getIconComponent(formData.icon);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <PawPrint className="w-6 h-6 text-emerald-400" />
          <h2 className="text-xl font-semibold text-white">Sekcije divjadi</h2>
          <span className="px-2 py-1 text-xs bg-stone-800 text-stone-400 rounded-full">
            {sections.length} sekcij
          </span>
        </div>
        {!showAddForm && (
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-emerald-600 hover:bg-emerald-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Dodaj sekcijo
          </Button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="bg-stone-800/50 border-stone-700/50 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingId ? 'Uredi sekcijo' : 'Dodaj novo sekcijo'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-300">Naslov (slovensko)</Label>
                <Input
                  value={formData.title_sl}
                  onChange={(e) => setFormData({ ...formData, title_sl: e.target.value })}
                  required
                  className="bg-stone-900/50 border-stone-700 text-white"
                  placeholder="npr. Jelenjad"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-300">Naslov (angleško)</Label>
                <Input
                  value={formData.title_en}
                  onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  required
                  className="bg-stone-900/50 border-stone-700 text-white"
                  placeholder="npr. Red Deer"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-300">Vsebina (slovensko)</Label>
              <Textarea
                value={formData.content_sl}
                onChange={(e) => setFormData({ ...formData, content_sl: e.target.value })}
                required
                rows={3}
                className="bg-stone-900/50 border-stone-700 text-white resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-stone-300">Vsebina (angleško)</Label>
              <Textarea
                value={formData.content_en}
                onChange={(e) => setFormData({ ...formData, content_en: e.target.value })}
                required
                rows={3}
                className="bg-stone-900/50 border-stone-700 text-white resize-none"
              />
            </div>

            {/* Icon Selection */}
            <div className="space-y-2">
              <Label className="text-stone-300">Ikona</Label>
              <div className="flex flex-wrap gap-2">
                {availableIcons.map((icon) => {
                  const Icon = icon.component;
                  return (
                    <button
                      key={icon.name}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon: icon.name })}
                      className={`p-3 rounded-lg transition-colors ${
                        formData.icon === icon.name
                          ? 'bg-emerald-600 text-white'
                          : 'bg-stone-700 text-stone-400 hover:bg-stone-600'
                      }`}
                      title={icon.label}
                    >
                      <Icon className="w-5 h-5" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div className="space-y-2">
              <Label className="text-stone-300">Barva</Label>
              <div className="flex flex-wrap gap-2">
                {availableColors.map((color) => (
                  <button
                    key={color.name}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.name })}
                    className={`w-10 h-10 rounded-lg ${color.class} transition-transform ${
                      formData.color === color.name
                        ? 'ring-2 ring-white ring-offset-2 ring-offset-stone-800 scale-110'
                        : 'hover:scale-105'
                    }`}
                    title={color.label}
                  />
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="p-4 bg-stone-900/50 rounded-lg">
              <Label className="text-stone-400 text-sm mb-2 block">Predogled</Label>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 bg-${formData.color}-600/20 rounded-xl flex items-center justify-center`}>
                  <IconComponent className={`w-6 h-6 text-${formData.color}-500`} />
                </div>
                <div>
                  <h4 className="text-white font-bold">{formData.title_sl || 'Naslov'}</h4>
                  <p className="text-stone-400 text-sm">{formData.content_sl?.substring(0, 100) || 'Vsebina...'}</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                className="bg-emerald-600 hover:bg-emerald-500"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingId ? 'Shrani spremembe' : 'Dodaj sekcijo'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                className="border-stone-700 text-stone-300 hover:bg-stone-800"
              >
                <X className="w-4 h-4 mr-2" />
                Prekliči
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Sections List */}
      <div className="grid gap-4">
        {sections.map((section) => {
          const Icon = getIconComponent(section.icon);
          return (
            <Card
              key={section.id}
              className="bg-stone-800/50 border-stone-700/50 p-4 hover:border-emerald-600/30 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 bg-${section.color}-600/20 rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-6 h-6 text-${section.color}-500`} />
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-semibold">{section.title_sl}</h4>
                  <p className="text-stone-400 text-sm line-clamp-2 mt-1">
                    {section.content_sl}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(section)}
                    className="border-stone-700 text-stone-300 hover:bg-stone-800"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(section.id)}
                    className="border-red-900/50 text-red-400 hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AdminWildlife;
